import { useCallback, useEffect, useState } from 'react';
import {
  safeAddEventListener,
  safeRemoveEventListener,
} from '../../utils/safeEventListener';

/**
 * Recursively finds an element matching `selector` inside open shadow roots.
 */
export function findInShadowDom(
  rootElement: Element | null,
  selector: string,
): Element | null {
  if (!rootElement) return null;

  const shadowRoot = rootElement.shadowRoot;
  if (!shadowRoot) return null;

  const direct = shadowRoot.querySelector(selector);
  if (direct) return direct;

  const elementsWithShadow = Array.from(
    shadowRoot.querySelectorAll('*'),
  ).filter((el) => Boolean(el.shadowRoot));

  for (const el of elementsWithShadow) {
    const found = findInShadowDom(el, selector);
    if (found) return found;
  }

  return null;
}

/**
 * Recursively observe every open shadow root under `root` (any depth).
 * Uses a WeakSet so each ShadowRoot is only registered once per observer lifetime.
 */
export const observeShadowTrees = (
  observer: MutationObserver,
  root: Element | null,
  observedShadows: WeakSet<ShadowRoot>,
) => {
  if (!root?.shadowRoot) return;

  const walk = (shadow: ShadowRoot) => {
    if (observedShadows.has(shadow)) return;
    observedShadows.add(shadow);
    observer.observe(shadow, { childList: true, subtree: true });

    shadow.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) {
        walk(el.shadowRoot);
      }
    });
  };

  walk(root.shadowRoot);
};

const SYNC_DEBOUNCE_MS = 32;

/**
 * Keeps a live reference to a ZDS menu host (e.g. z-multi-org-menu) inside the
 * nav shadow DOM. z-navigation tears down and recreates these hosts whenever
 * its `data` changes, so we must re-query after rebuilds instead of caching
 * the first node forever.
 *
 * Observation is scoped to the nav custom element + its shadow trees (not the
 * whole page). `navigation-changed` is a backup signal for rebuilds.
 */
export function useZdsMenuHost({
  selector,
  isVisible = true,
  navRootElement,
}: {
  selector: string;
  isVisible?: boolean;
  navRootElement: Element | null;
}): Element | null {
  const [menuElement, setMenuElement] = useState<Element | null>(null);

  const findMenu = useCallback((): Element | null => {
    if (!isVisible) return null;
    const root = navRootElement || document.querySelector('z-unified-nav-v2');
    return findInShadowDom(root, selector);
  }, [isVisible, navRootElement, selector]);

  useEffect(() => {
    if (!isVisible) {
      setMenuElement(null);
      return;
    }

    const getRoot = () =>
      navRootElement || document.querySelector('z-unified-nav-v2');

    const observedShadows = new WeakSet<ShadowRoot>();
    const observedLightRoots = new WeakSet<Element>();
    let debounceTimer: number | null = null;
    let bodyObserver: MutationObserver | null = null;
    const host: { observer?: MutationObserver } = {};

    const applyFound = (found: Element | null) => {
      setMenuElement((current) => {
        if (found && found !== current) {
          return found;
        }
        if (current && !current.isConnected) {
          return found;
        }
        if (!found) {
          return current?.isConnected ? current : null;
        }
        return current;
      });
    };

    const attachNavObservers = (observer: MutationObserver, root: Element) => {
      if (!observedLightRoots.has(root)) {
        observedLightRoots.add(root);
        observer.observe(root, { childList: true, subtree: true });
      }
      observeShadowTrees(observer, root, observedShadows);
    };

    const sync = () => {
      const root = getRoot();
      const found = findMenu();
      applyFound(found);

      if (root && host.observer) {
        attachNavObservers(host.observer, root);
        // Nav is available — stop watching the whole document
        if (bodyObserver) {
          bodyObserver.disconnect();
          bodyObserver = null;
        }
      }
    };

    const scheduleSync = () => {
      if (debounceTimer != null) return;
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        sync();
      }, SYNC_DEBOUNCE_MS);
    };

    host.observer = new MutationObserver(scheduleSync);

    sync();

    const root = getRoot();
    if (root) {
      attachNavObservers(host.observer, root);
    } else {
      // Nav custom element not mounted yet — watch body only until it appears
      bodyObserver = new MutationObserver(scheduleSync);
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Backup when z-navigation rebuilds hosts after data assignment.
    // Registration has to go through the same implementation as the removal
    // below, or the two would work on different listener registries.
    safeAddEventListener(document, 'navigation-changed', sync);

    return () => {
      if (debounceTimer != null) {
        window.clearTimeout(debounceTimer);
      }
      host.observer?.disconnect();
      bodyObserver?.disconnect();
      safeRemoveEventListener(document, 'navigation-changed', sync);
    };
  }, [isVisible, findMenu, navRootElement]);

  return menuElement;
}
